import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useMemo, useState } from "react";

import HistoryIcon from "@mui/icons-material/History";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { SxProps } from "@mui/material/styles";

import { orderBy, take } from "lodash";

import { TAG_HISTORY_FEATURE } from "constants/feature-flags";
import { TagTypes } from "constants/tags";
import { AssignedTag } from "services/types/assigned-tag.type";
import { Tag } from "services/types/tags.type";

import AddAssignedTag from "./add-assigned-tag.component";
import AssignedTagsConfirmModal from "./assigned-tags-confirm-modal.component";
import TagChip from "./tag-chip.component";
import TagHistoryDialog from "./tag-history-dialog.component";

type AssignedTagsListProp = {
  showDotsOnly?: boolean | string;
  dotsSize?: string;
  tags?: AssignedTag[];
  tagType: TagTypes;
  displayLimit?: number;
  sx?: SxProps;
  disableUpdateMutation?: boolean;
  onDelete?: (tag_id: number, index?: number) => void;
  onAdd?: (tag_id: number, tag: Tag) => void;
  onUpdate?: (tag: AssignedTag) => void;
  filterActiveOnly?: boolean;
  dense?: boolean;
  /**
   * Decouples the chip's click-to-edit pop-over from `onDelete`. A surface that wants removal
   * in place but not editing in place (the Deliveries grid's Tags cell) sets it; every other
   * consumer leaves it off and is unchanged.
   */
  disableChipEdit?: boolean;
  /**
   * The id of the record these tags belong to. Supplying it opts the surface into the tag
   * history button — read-only display surfaces (report cells, dots-only previews) leave it
   * unset and are unchanged.
   */
  modelId?: number;
  /** Names the record in the history dialog subtitle. */
  historySubtitle?: string;
};

export default function AssignedTagsList({
  tags,
  tagType,
  displayLimit,
  onAdd,
  showDotsOnly = false,
  dotsSize = "8px",
  disableUpdateMutation,
  onDelete,
  onUpdate,
  sx,
  filterActiveOnly = true,
  dense = false,
  disableChipEdit = false,
  modelId,
  historySubtitle
}: AssignedTagsListProp) {
  const flags = useFlags();
  const [historyOpen, setHistoryOpen] = useState(false);

  // Rendered even when the record currently shows NO chips — that is the case the feature
  // exists for. A record whose only tag was removed is indistinguishable from one that was
  // never tagged, so hanging the entry point off a chip would hide exactly what someone came
  // looking for.
  const canShowHistory =
    Boolean(flags[TAG_HISTORY_FEATURE]) && !showDotsOnly && Boolean(modelId);
  const { displayTags, style } = useMemo(() => {
    const displayTags = !displayLimit
      ? tags
      : take(orderBy(tags || [], ["id"], ["desc"]), displayLimit);

    const style = displayTags?.length ? { ...sx, pt: 0.5 } : sx;

    return {
      displayTags,
      style
    };
  }, [displayLimit, sx, tags]);

  const hiddenCount = (tags ?? []).length - (displayTags ?? []).length;

  const [confirmationOptions, setConfirmationOptions] = useState<{
    show: boolean;
    title: string;
    tag_id?: number;
  }>({
    show: false,
    title: "",
    tag_id: undefined
  });

  const handleCancelConfirm = useCallback(() => {
    setConfirmationOptions({
      show: false,
      title: "",
      tag_id: undefined
    });
  }, []);

  const handleSubmitConfirm = useCallback(() => {
    onDelete?.(Number(confirmationOptions?.tag_id));
    handleCancelConfirm();
  }, [handleCancelConfirm, onDelete, confirmationOptions]);

  const handleDelete = useCallback(
    (tag_id: number) => {
      const tag = tags?.find((tag) => tag.id === tag_id);
      setConfirmationOptions({
        show: true,
        title: `Do you want to remove "${tag?.tag?.name || ""}" tag?`,
        tag_id
      });
    },
    [tags]
  );

  return (
    <Box sx={style}>
      {displayTags?.filter(Boolean).map((assignedTag) =>
        showDotsOnly ? (
          <Box
            key={assignedTag.id}
            sx={{
              display: "inline-block",
              borderRadius: "50%",
              marginRight: "4px",
              width: dotsSize,
              height: dotsSize,
              background: assignedTag.tag.color
            }}
          />
        ) : (
          <TagChip
            key={assignedTag.id}
            tagType={tagType}
            assignedTag={assignedTag}
            onDelete={onDelete ? handleDelete : undefined}
            onUpdate={onUpdate}
            canEdit={Boolean(onDelete) && !disableChipEdit}
            disableUpdateMutation={disableUpdateMutation}
            dense={dense}
          />
        )
      )}
      {!showDotsOnly && Boolean(hiddenCount) && (
        <Typography variant="body1" display="inline-block">
          +{hiddenCount}&nbsp;more
        </Typography>
      )}
      {onAdd && (
        <AddAssignedTag
          tagType={tagType}
          existingTags={tags ?? []}
          onAdd={onAdd}
          filterActiveOnly={filterActiveOnly}
        />
      )}
      {canShowHistory && (
        <Tooltip title="Tag history">
          <IconButton
            size="small"
            aria-label="Tag history"
            data-testid="tag-history-button"
            onClick={() => setHistoryOpen(true)}
            sx={{ verticalAlign: "middle" }}
          >
            <HistoryIcon sx={{ width: 16, height: 16 }} />
          </IconButton>
        </Tooltip>
      )}
      {canShowHistory && historyOpen && (
        <TagHistoryDialog
          open={historyOpen}
          onClose={() => setHistoryOpen(false)}
          tagType={tagType}
          modelId={modelId as number}
          subtitle={historySubtitle}
        />
      )}
      <AssignedTagsConfirmModal
        open={confirmationOptions?.show}
        title={confirmationOptions?.title}
        onCancel={handleCancelConfirm}
        onChange={handleSubmitConfirm}
      />
    </Box>
  );
}
